'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || ''
const isEnabled = /^G-[A-Z0-9]+$/.test(measurementId)

declare global {
  interface Window {
    dataLayer?: IArguments[]
    gtag?: (...args: unknown[]) => void
    __turnoGaInitialized?: boolean
  }
}

function getPublicAnalyticsPath(pathname: string): string | null {
  // The middleware rewrites `/` to `/landing` while keeping `/` in the
  // browser address bar. Attribute both entry points to the landing's
  // canonical URL so the home page is not missed or split in analytics.
  if (pathname === '/' || pathname === '/landing') return '/landing'
  if (/^\/[a-z0-9][a-z0-9-]*(?:\/(?:booking|services|barbers))?\/?$/.test(pathname)) {
    return pathname.replace(/\/$/, '') || '/'
  }
  return null
}

function ensureGoogleAnalytics() {
  if (!isEnabled || window.__turnoGaInitialized) return

  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function (...args: unknown[]) {
    window.dataLayer?.push(args as unknown as IArguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
  })
  window.__turnoGaInitialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  script.dataset.gaMeasurementId = measurementId
  document.head.appendChild(script)

  document.addEventListener('click', (event) => {
    const pagePath = getPublicAnalyticsPath(window.location.pathname)
    if (!pagePath) return
    const target = event.target instanceof Element ? event.target : null
    const link = target?.closest('a')
    if (!link || new URL(link.href, window.location.origin).hostname !== 'wa.me') return
    window.gtag?.('event', 'generate_lead', {
      method: 'whatsapp',
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
      page_title: document.title,
    })
  })
}

export function trackAppointmentConversion() {
  if (typeof window === 'undefined') return
  // Si la confirmación ocurrió en una ruta privada con token, atribuirla a la
  // landing y no enviar el token ni la ruta operativa a Analytics.
  const pagePath = getPublicAnalyticsPath(window.location.pathname) || '/landing'
  if (!pagePath || typeof window.gtag !== 'function') return

  window.gtag('event', 'book_appointment', {
    method: 'online_booking',
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: document.title,
  })
}

export default function GoogleAnalytics() {
  const pathname = usePathname()

  useEffect(() => {
    const pagePath = getPublicAnalyticsPath(pathname)
    if (!isEnabled || !pagePath) return
    ensureGoogleAnalytics()
    window.gtag?.('event', 'page_view', {
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
      page_title: document.title,
    })
  }, [pathname])

  return null
}
